/**
 * In-call "Speaker" toggle. Browsers can only route call audio via setSinkId, and only between the
 * outputs they expose — Android Chrome lists a speakerphone output on some devices, desktops list
 * their speakers. iOS exposes no route choice while the mic is live, so there the toggle is hidden
 * (users switch from Control Center).
 */

export interface OutputDevice {
  deviceId: string;
  kind: string;
  label: string;
}

export interface SpeakerPlan {
  /** Output to use when "Speaker" is on. */
  speakerId: string;
  /** Output to return to when it's switched off ('' = system default). */
  otherId: string;
}

const SPEAKER = /speaker/i;
const PERSONAL = /headset|headphone|earpiece|airpods|buds|bluetooth|hands-?free/i;

export function canChooseOutput(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

/** Null when there's no meaningful speaker/non-speaker pair to toggle between. */
export function planSpeaker(devices: OutputDevice[]): SpeakerPlan | null {
  const outs = devices.filter((d) => d.kind === 'audiooutput' && d.deviceId);
  const real = outs.filter((d) => d.deviceId !== 'default' && d.deviceId !== 'communications');
  const speaker = real.find((d) => SPEAKER.test(d.label) && !PERSONAL.test(d.label));
  if (!speaker) return null;
  // Chrome labels the default entry "Default - <device>": if that's already the speaker, "off" must
  // mean some other (personal) output, otherwise it's simply the system default.
  const def = outs.find((d) => d.deviceId === 'default');
  const defaultIsSpeaker = def ? def.label.includes(speaker.label) : false;
  if (!defaultIsSpeaker) return { speakerId: speaker.deviceId, otherId: '' };
  const other = real.find((d) => d.deviceId !== speaker.deviceId);
  return other ? { speakerId: speaker.deviceId, otherId: other.deviceId } : null;
}
